import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Play } from 'lucide-react';
import { useContent } from '../context/ContentContext';
import { trackButtonClick } from '../services/api';

// Floating particles component
const FloatingParticles = () => {
  const particles = Array.from({ length: 50 }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    size: Math.random() * 4 + 1,
    duration: Math.random() * 20 + 10,
    delay: Math.random() * 5,
  }));

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map((particle) => (
        <motion.div
          key={particle.id}
          className="absolute rounded-full bg-white"
          style={{
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            width: particle.size,
            height: particle.size,
          }}
          animate={{
            y: [0, -100, 0],
            opacity: [0, 0.6, 0],
            scale: [0, 1, 0],
          }}
          transition={{
            duration: particle.duration,
            repeat: Infinity,
            delay: particle.delay,
            ease: "easeInOut",
          }}
        />
      ))}
    </div>
  );
};

// Animated grid lines
const GridLines = () => {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-[0.03]">
      <div className="absolute inset-0" style={{
        backgroundImage: `
          linear-gradient(rgba(20, 184, 166, 0.5) 1px, transparent 1px),
          linear-gradient(90deg, rgba(20, 184, 166, 0.5) 1px, transparent 1px)
        `,
        backgroundSize: '100px 100px',
      }} />
    </div>
  );
};

// Animated waves
const AnimatedWaves = () => {
  return (
    <div className="absolute bottom-0 left-0 right-0 h-64 overflow-hidden pointer-events-none">
      <svg
        className="absolute bottom-0 w-full"
        viewBox="0 0 1440 320"
        preserveAspectRatio="none"
        style={{ height: '100%', width: '200%' }}
      >
        <motion.path
          d="M0,192L48,197.3C96,203,192,213,288,229.3C384,245,480,267,576,250.7C672,235,768,181,864,181.3C960,181,1056,235,1152,234.7C1248,235,1344,181,1392,154.7L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
          fill="url(#wave-gradient-1)"
          animate={{
            d: [
              "M0,192L48,197.3C96,203,192,213,288,229.3C384,245,480,267,576,250.7C672,235,768,181,864,181.3C960,181,1056,235,1152,234.7C1248,235,1344,181,1392,154.7L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z",
              "M0,128L48,154.7C96,181,192,235,288,234.7C384,235,480,181,576,181.3C672,181,768,235,864,250.7C960,267,1056,245,1152,229.3C1248,213,1344,203,1392,197.3L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z",
              "M0,192L48,197.3C96,203,192,213,288,229.3C384,245,480,267,576,250.7C672,235,768,181,864,181.3C960,181,1056,235,1152,234.7C1248,235,1344,181,1392,154.7L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z",
            ],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        <defs>
          <linearGradient id="wave-gradient-1" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="rgba(20, 184, 166, 0.1)" />
            <stop offset="50%" stopColor="rgba(59, 130, 246, 0.1)" />
            <stop offset="100%" stopColor="rgba(245, 158, 11, 0.05)" />
          </linearGradient>
        </defs>
      </svg>
    </div>
  );
};

// Rotating rings
const RotatingRings = () => {
  return (
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
      {[600, 500, 400].map((size, i) => (
        <motion.div
          key={size}
          className="absolute rounded-full border"
          style={{
            width: size,
            height: size,
            left: -size / 2,
            top: -size / 2,
            borderColor: i === 0 ? 'rgba(20, 184, 166, 0.1)' : i === 1 ? 'rgba(59, 130, 246, 0.08)' : 'rgba(245, 158, 11, 0.06)',
          }}
          animate={{
            rotate: i % 2 === 0 ? 360 : -360,
          }}
          transition={{
            duration: 30 + i * 10,
            repeat: Infinity,
            ease: "linear",
          }}
        />
      ))}
    </div>
  );
};

// Glowing orbs
const GlowingOrbs = () => {
  return (
    <>
      <motion.div
        animate={{
          scale: [1, 1.3, 1],
          opacity: [0.2, 0.4, 0.2],
          x: [0, 50, 0],
          y: [0, -30, 0],
        }}
        transition={{
          duration: 12,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full bg-gradient-to-br from-[var(--accent-teal)] to-transparent blur-[120px]"
      />
      <motion.div
        animate={{
          scale: [1.2, 1, 1.2],
          opacity: [0.15, 0.3, 0.15],
          x: [0, -50, 0],
          y: [0, 30, 0],
        }}
        transition={{
          duration: 15,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full bg-gradient-to-br from-[var(--accent-blue)] to-transparent blur-[120px]"
      />
      <motion.div
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.1, 0.25, 0.1],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-gradient-radial from-[var(--accent-gold)]/20 to-transparent blur-[100px]"
      />
    </>
  );
};

const Hero = () => {
  const { heroContent } = useContent();

  const handleBookCallClick = () => {
    trackButtonClick('hero-book-call', heroContent.primaryCta, '/');
  };

  const handleExploreClick = () => {
    trackButtonClick('hero-explore-programs', heroContent.secondaryCta, '/');
  };

  return (
    <section className="hero-bg min-h-screen flex items-start justify-center relative overflow-hidden pt-32 md:pt-40" data-testid="hero-section">
      {/* Layered Animated Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Base gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[var(--bg-page)] to-[var(--bg-page)]" />
        
        {/* Grid lines */}
        <GridLines />
        
        {/* Rotating rings */}
        <RotatingRings />
        
        {/* Glowing orbs */}
        <GlowingOrbs />
        
        {/* Floating particles */}
        <FloatingParticles />
        
        {/* Animated waves at bottom */}
        <AnimatedWaves />
        
        {/* Noise texture */}
        <div className="absolute inset-0 opacity-[0.015]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
        }} />
      </div>

      <div className="container-main relative z-10">
        <div className="max-w-5xl mx-auto text-center">
          {/* Main Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold leading-tight mb-8 md:mb-10 px-2"
          >
            <span className="gradient-text">Your Expertise Deserves to Be Heard.</span>
            <br />
            <span className="text-[var(--text-primary)]">Your Leadership Deserves to Be Felt.</span>
          </motion.h1>

          {/* Mission Statement */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-semibold max-w-4xl mx-auto mb-8 md:mb-10 leading-relaxed px-4 text-center"
          >
            <span className="text-[var(--accent-teal)]">CommfluentBiz</span>
            <span className="text-[var(--text-primary)]"> transforms </span>
            <span className="text-[var(--accent-gold)]">communication</span>
            <span className="text-[var(--text-primary)]"> into </span>
            <span className="gradient-text">influence</span>
            <span className="text-[var(--text-primary)]">, </span>
            <span className="text-[var(--accent-gold)]">leadership</span>
            <span className="text-[var(--text-primary)]"> into </span>
            <span className="gradient-text">impact</span>
            <span className="text-[var(--text-primary)]">, and </span>
            <span className="text-[var(--accent-teal)]">human potential</span>
            <span className="text-[var(--text-primary)]"> into </span>
            <span className="text-[var(--accent-gold)]">lasting success</span>
            <span className="text-[var(--text-primary)]">.</span>
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4 px-4"
          >
            <motion.a 
              href={heroContent.calendlyUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleBookCallClick}
              className="btn-primary text-sm md:text-base px-6 md:px-8 py-3 md:py-4 w-full sm:w-auto justify-center magnetic-btn"
              data-testid="hero-book-call-btn"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
            >
              {heroContent.primaryCta}
              <ArrowRight className="w-4 md:w-5 h-4 md:h-5" />
            </motion.a>
            <motion.a 
              href="#programs"
              onClick={handleExploreClick}
              className="btn-secondary text-sm md:text-base px-6 md:px-8 py-3 md:py-4 w-full sm:w-auto justify-center"
              data-testid="hero-explore-btn"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
            >
              <Play className="w-4 md:w-5 h-4 md:h-5" />
              {heroContent.secondaryCta}
            </motion.a>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="w-6 h-10 rounded-full border-2 border-[var(--border-accent)] flex justify-center pt-2"
        >
          <motion.div
            animate={{ opacity: [1, 0, 1] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="w-1 h-2 bg-[var(--accent-teal)] rounded-full"
          />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
